/**
 * The Aurora Atlas design system for learn.4klabs.ai.
 *
 * The portal is an atlas: every class is a plate, every subject a coloured
 * region. Colour never arrives as a flat fill — it arrives as LIGHT. Each page
 * carries an aurora generated from the subjects actually on it, so the colour of
 * a screen IS the curriculum: the home page mixes all six subjects, a Class 10
 * page mixes exactly its Science/Maths/Social-Science three, and a lesson page
 * glows its single subject. Glass panels float on that light; an editorial serif
 * carries the type.
 *
 * Dark mode is driven by a `.dark` class on <html> (Tailwind class strategy).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Sun, Moon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { initializeTheme } from '../../hooks/useTheme';

// ----------------------------------------------------------------------------
// Theme
// ----------------------------------------------------------------------------

export type LearnTheme = 'light' | 'dark';

export function useLearnTheme() {
  const [theme, setTheme] = useState<LearnTheme>(() => {
    if (typeof window === 'undefined') return 'light';
    const saved = window.localStorage.getItem('learn-theme');
    if (saved === 'light' || saved === 'dark') return saved;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const toggle = useCallback(() => {
    setTheme((t) => {
      const next: LearnTheme = t === 'dark' ? 'light' : 'dark';
      try { window.localStorage.setItem('learn-theme', next); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // The learn theme must own the `.dark` class on <html>, NOT just a nested wrapper.
  // The global app theme (initializeTheme/useTheme) writes `.dark` to document.documentElement
  // from the OS preference; Tailwind's class strategy then keys every `dark:` utility off that
  // ancestor, so a nested `.dark` wrapper can never un-dark the page while <html>.dark is set
  // (which is always, when macOS is in dark mode). Drive <html> here so the toggle actually wins,
  // and hand control back to the global app theme when leaving the learn pages.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);
  useEffect(() => () => { initializeTheme(); }, []);

  return { theme, toggle };
}

/** Outer wrapper that applies the dark class + page background.
 *  The app sets `html,body,#root { height:100%; overflow:hidden }` for the fixed
 *  chat layout, so the learn pages must be their own full-height scroll container
 *  (document-flow scrolling would be clipped). */
export function LearnRoot({ theme, className, subjects, children }: {
  theme: LearnTheme;
  className?: string;
  /** The subjects present on this page — the aurora is mixed from exactly these. */
  subjects?: string[];
  children: React.ReactNode;
}) {
  // `dark` must sit on an ANCESTOR of the elements using `dark:` utilities (Tailwind
  // class strategy generates `.dark .dark\:foo`). Putting it on the same element as
  // the bg/text utilities would leave the page chrome un-themed — hence the wrapper.
  return (
    <div className={cn('h-full', theme === 'dark' && 'dark')}>
      <div className={cn('learn-type relative h-full overflow-y-auto bg-[#FBFAF7] text-[#191831] antialiased dark:bg-[#0D0E16] dark:text-[#EFEEF8]', className)}>
        {subjects && <LearnAurora subjects={subjects} />}
        <div className="relative">{children}</div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// The aurora — the signature
// ----------------------------------------------------------------------------

/**
 * The page's background wash, mixed from the subjects on that page.
 *
 * Built as radial-gradient background layers on a single element rather than a
 * stack of blurred divs: `filter: blur()` on several large layers is the exact
 * cost a school Chromebook cannot pay, while a radial gradient is already soft
 * and costs one paint. Positions are derived from a hash of the subject list, so
 * two different six-subject pages don't rhyme, and the same page is stable
 * across reloads. The drift is a slow transform on a wrapper (compositor-only).
 */
export function LearnAurora({ subjects, className }: { subjects: string[]; className?: string }) {
  const keys = subjects.filter((s) => SUBJECT_MAP[s]);
  // A single-subject page still needs depth: render that one hue as several
  // pools at different luminosities rather than borrowing a colour it has no
  // claim to. A page with no known subject falls back to the house violet.
  const hues = keys.length >= 3 ? keys.map((k) => SUBJECT_MAP[k].aurora)
    : keys.length > 0 ? keys.flatMap((k) => [SUBJECT_MAP[k].aurora, SUBJECT_MAP[k].bright, SUBJECT_MAP[k].aurora])
    : ['#CBB8F5', '#B7D9F2', '#F3DCC0'];

  const seed = hashStr(keys.join('|') || 'house');
  const pools = hues.slice(0, 6).map((hue, i) => {
    // Deterministic placement on a ring, jittered by the seed.
    const a = (i / Math.min(hues.length, 6)) * Math.PI * 2 + (seed % 100) / 100;
    const x = Math.round(50 + Math.cos(a) * (26 + ((seed >> (i + 1)) % 16)));
    const y = Math.round(38 + Math.sin(a) * (24 + ((seed >> (i + 2)) % 14)));
    const r = 42 + ((seed >> (i + 3)) % 22);
    return `radial-gradient(${r}% ${r * 0.9}% at ${x}% ${y}%, ${hue} 0%, transparent 68%)`;
  });

  return (
    <div aria-hidden="true" className={cn('pointer-events-none fixed inset-0 z-0 overflow-hidden', className)}>
      <div
        className="learn-aurora absolute -inset-[18%] opacity-[0.85] dark:opacity-[0.55]"
        style={{ backgroundImage: pools.join(', '), backgroundColor: 'transparent' }}
      />
      {/* A whisper of paper grain keeps the wash from reading as a stock gradient. */}
      <div className="absolute inset-0 bg-[#FBFAF7]/25 dark:bg-[#0D0E16]/30" />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Subjects
// ----------------------------------------------------------------------------

/**
 * The subject spectrum — each CBSE subject owns a hue, used STRUCTURALLY:
 * chapter bands, course covers, filter chips, progress accents. The interface
 * itself stays paper-and-ink; the colour in the page comes from what is being
 * studied (and from the lesson thumbnails), not from decoration.
 */
interface SubjectMeta {
  key: 'math' | 'phys' | 'chem' | 'science' | 'excel' | 'bio' | 'soc' | 'default';
  color: string;      // dense — text on light, fills at low alpha
  bright: string;     // luminous — gradient ends, dark-mode text
  gradient: string;   // the subject's band / cover wash
  aurora: string;     // high-luminosity — the colour this subject contributes to the page light
}
const SUBJECT_MAP: Record<string, SubjectMeta> = {
  Science:          { key: 'science', color: '#0E8A80', bright: '#2DD4BF', gradient: 'linear-gradient(120deg,#0E8A80,#34D399)', aurora: '#A8E4DC' },
  Physics:          { key: 'phys',    color: '#2458D6', bright: '#38BDF8', gradient: 'linear-gradient(120deg,#2458D6,#22D3EE)', aurora: '#B4CDF7' },
  Chemistry:        { key: 'chem',    color: '#B0329E', bright: '#F472B6', gradient: 'linear-gradient(120deg,#A21CAF,#F472B6)', aurora: '#F4C2E4' },
  Biology:          { key: 'bio',     color: '#1F9D4D', bright: '#84CC16', gradient: 'linear-gradient(120deg,#16A34A,#A3E635)', aurora: '#CBE8A8' },
  Mathematics:      { key: 'math',    color: '#6D28D9', bright: '#A78BFA', gradient: 'linear-gradient(120deg,#6D28D9,#A78BFA)', aurora: '#CBB8F5' },
  'Social Science': { key: 'soc',     color: '#C2660A', bright: '#FBBF24', gradient: 'linear-gradient(120deg,#D97706,#FBBF24)', aurora: '#F7DCAE' },
  Excel:            { key: 'excel',   color: '#0B8457', bright: '#34D399', gradient: 'linear-gradient(120deg,#059669,#34D399)', aurora: '#A9E3C8' },
};
/** The full spectrum, in display order — every all-subject surface mixes from these. */
export const LEARN_ALL_SUBJECTS = [
  'Science',
  'Physics',
  'Chemistry',
  'Biology',
  'Mathematics',
  'Social Science',
] as const as unknown as string[];

export function subjectMeta(subject?: string | null): SubjectMeta {
  return (
    (subject && SUBJECT_MAP[subject]) || {
      key: 'default',
      color: '#7c7a8b',
      bright: '#a8a4b8',
      gradient: 'linear-gradient(120deg,#6b6880,#a8a4b8)',
      aurora: '#D8D4E4',
    }
  );
}
/** Ordered stops for a class cover: the colours of the subjects it contains. */
export function subjectStops(subjects: string[]): string[] {
  const seen = subjects.filter((s) => SUBJECT_MAP[s]).map((s) => SUBJECT_MAP[s].color);
  return seen.length ? seen : ['#6b6880'];
}

/** Editorial, muted per-subject artwork used as the card / hero backdrop. */
export function SubjectArt({ subject, className }: { subject?: string | null; className?: string }) {
  const { key } = subjectMeta(subject);
  const common = { className: cn('h-full w-full', className), preserveAspectRatio: 'xMidYMid slice' as const, xmlns: 'http://www.w3.org/2000/svg' };
  if (key === 'math') {
    return (
      <svg viewBox="0 0 400 250" {...common}>
        <defs><linearGradient id="lg-m" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#222a4d" /><stop offset="1" stopColor="#13162b" /></linearGradient></defs>
        <rect width="400" height="250" fill="url(#lg-m)" />
        <g stroke="rgba(143,156,255,.10)" strokeWidth="1">
          {Array.from({ length: 11 }).map((_, i) => <line key={`v${i}`} x1={i * 40} y1="0" x2={i * 40} y2="250" />)}
          {Array.from({ length: 7 }).map((_, i) => <line key={`h${i}`} x1="0" y1={i * 40} x2="400" y2={i * 40} />)}
        </g>
        <line x1="0" y1="190" x2="400" y2="190" stroke="rgba(143,156,255,.3)" strokeWidth="1.5" />
        <line x1="200" y1="0" x2="200" y2="250" stroke="rgba(143,156,255,.3)" strokeWidth="1.5" />
        <path d="M52 200 Q200 -8 348 200" fill="none" stroke="#8e9cff" strokeWidth="2.5" />
        <circle cx="200" cy="96" r="4" fill="#fff" /><circle cx="128" cy="150" r="3.5" fill="#aeb8ff" /><circle cx="272" cy="150" r="3.5" fill="#aeb8ff" />
      </svg>
    );
  }
  if (key === 'phys') {
    return (
      <svg viewBox="0 0 400 250" {...common}>
        <defs><linearGradient id="lg-p" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#16403a" /><stop offset="1" stopColor="#0c1d1a" /></linearGradient></defs>
        <rect width="400" height="250" fill="url(#lg-p)" />
        <path d="M0 125 C50 40,90 40,140 125 S230 210,280 125 S370 40,400 125" fill="none" stroke="#5fbfb0" strokeWidth="2.5" />
        <path d="M0 125 C50 200,90 200,140 125 S230 50,280 125 S370 200,400 125" fill="none" stroke="rgba(95,191,176,.32)" strokeWidth="1.5" />
        <line x1="200" y1="34" x2="200" y2="128" stroke="rgba(255,255,255,.35)" strokeWidth="1.5" />
        <circle cx="200" cy="136" r="11" fill="none" stroke="#5fbfb0" strokeWidth="2" /><circle cx="200" cy="136" r="4" fill="#5fbfb0" />
      </svg>
    );
  }
  if (key === 'chem') {
    return (
      <svg viewBox="0 0 400 250" {...common}>
        <defs><linearGradient id="lg-c" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#3d2030" /><stop offset="1" stopColor="#1a0f17" /></linearGradient></defs>
        <rect width="400" height="250" fill="url(#lg-c)" />
        {Array.from({ length: 7 }).map((_, c) => Array.from({ length: 3 }).map((__, r) => (
          <rect key={`${c}-${r}`} x={66 + c * 40} y={58 + r * 40} width="34" height="34" rx="5" fill={`rgba(214,121,168,${0.05 + ((c * 2 + r) % 5) * 0.07})`} stroke="rgba(214,121,168,.38)" strokeWidth="1" />
        )))}
      </svg>
    );
  }
  if (key === 'science') {
    // Solar system: sun + orbital rings + planets over deep space
    return (
      <svg viewBox="0 0 400 250" {...common}>
        <defs>
          <radialGradient id="lg-s" cx="22%" cy="50%" r="90%"><stop offset="0" stopColor="#1c2348" /><stop offset="1" stopColor="#080a18" /></radialGradient>
          <radialGradient id="sun-s" cx="50%" cy="50%" r="50%"><stop offset="0" stopColor="#ffd27a" /><stop offset="1" stopColor="#e0913a" /></radialGradient>
        </defs>
        <rect width="400" height="250" fill="url(#lg-s)" />
        {[[60,40],[330,60],[300,200],[120,210],[210,30],[370,170]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={i % 2 ? 1.3 : 0.9} fill="rgba(255,255,255,.6)" />)}
        {[58, 96, 140].map((rx, i) => <ellipse key={i} cx="88" cy="125" rx={rx} ry={rx * 0.62} fill="none" stroke="rgba(201,138,62,.28)" strokeWidth="1" />)}
        <circle cx="88" cy="125" r="22" fill="url(#sun-s)" />
        <circle cx="88" cy="125" r="30" fill="none" stroke="rgba(255,210,122,.25)" strokeWidth="6" />
        <circle cx="146" cy="125" r="4.5" fill="#9fb4ff" />
        <circle cx="184" cy="86" r="6.5" fill="#d98b6a" />
        <circle cx="228" cy="172" r="5" fill="#7fd0c0" />
      </svg>
    );
  }
  if (key === 'excel') {
    // Spreadsheet grid with a highlighted formula cell + a mini bar chart
    return (
      <svg viewBox="0 0 400 250" {...common}>
        <defs><linearGradient id="lg-x" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#103b2a" /><stop offset="1" stopColor="#08160f" /></linearGradient></defs>
        <rect width="400" height="250" fill="url(#lg-x)" />
        <g stroke="rgba(33,163,102,.18)" strokeWidth="1">
          {Array.from({ length: 9 }).map((_, i) => <line key={`v${i}`} x1={40 + i * 40} y1="40" x2={40 + i * 40} y2="210" />)}
          {Array.from({ length: 5 }).map((_, i) => <line key={`h${i}`} x1="40" y1={40 + i * 34} x2="360" y2={40 + i * 34} />)}
        </g>
        <rect x="40" y="40" width="320" height="34" fill="rgba(33,163,102,.16)" />
        <rect x="120" y="108" width="40" height="34" fill="rgba(52,211,153,.28)" stroke="#34d399" strokeWidth="1.5" />
        <g fill="#34d399">
          <rect x="252" y="150" width="14" height="44" rx="2" /><rect x="272" y="132" width="14" height="62" rx="2" /><rect x="292" y="160" width="14" height="34" rx="2" /><rect x="312" y="118" width="14" height="76" rx="2" />
        </g>
      </svg>
    );
  }
  if (key === 'bio') {
    // Microbiology: glowing cells / microbes over a dark microscopic field
    return (
      <svg viewBox="0 0 400 250" {...common}>
        <defs><radialGradient id="lg-b" cx="40%" cy="45%" r="80%"><stop offset="0" stopColor="#143b30" /><stop offset="1" stopColor="#08160f" /></radialGradient></defs>
        <rect width="400" height="250" fill="url(#lg-b)" />
        {[[110, 120, 34], [250, 90, 26], [300, 180, 20], [70, 200, 16], [200, 200, 22], [330, 70, 14]].map(([x, y, r], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r={r} fill="rgba(63,174,122,0.10)" stroke="rgba(95,199,150,0.5)" strokeWidth="1.5" />
            <circle cx={x} cy={y} r={r * 0.35} fill="rgba(95,199,150,0.4)" />
          </g>
        ))}
        {[[40, 60], [370, 130], [160, 40], [280, 230]].map(([x, y], i) => <circle key={`p${i}`} cx={x} cy={y} r="1.5" fill="rgba(150,230,190,.7)" />)}
      </svg>
    );
  }
  if (key === 'soc') {
    return (
      <svg viewBox="0 0 400 250" {...common}>
        <defs><linearGradient id="lg-soc" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#3d2c12" /><stop offset="1" stopColor="#191007" /></linearGradient></defs>
        <rect width="400" height="250" fill="url(#lg-soc)" />
        <circle cx="200" cy="128" r="86" fill="none" stroke="rgba(251,191,36,.4)" strokeWidth="1.5" />
        <ellipse cx="200" cy="128" rx="86" ry="30" fill="none" stroke="rgba(251,191,36,.28)" strokeWidth="1" />
        <ellipse cx="200" cy="128" rx="86" ry="62" fill="none" stroke="rgba(251,191,36,.2)" strokeWidth="1" />
        <ellipse cx="200" cy="128" rx="34" ry="86" fill="none" stroke="rgba(251,191,36,.28)" strokeWidth="1" />
        <ellipse cx="200" cy="128" rx="66" ry="86" fill="none" stroke="rgba(251,191,36,.18)" strokeWidth="1" />
        <path d="M60 200 C130 150, 260 110, 352 62" fill="none" stroke="#FBBF24" strokeWidth="2" strokeDasharray="1 7" strokeLinecap="round" />
        <circle cx="60" cy="200" r="4" fill="#FBBF24" /><circle cx="352" cy="62" r="4" fill="#FBBF24" />
        <circle cx="222" cy="96" r="2.5" fill="rgba(251,191,36,.8)" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 400 250" {...common}>
      <defs><linearGradient id="lg-d" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#26252e" /><stop offset="1" stopColor="#131218" /></linearGradient></defs>
      <rect width="400" height="250" fill="url(#lg-d)" />
      <g stroke="rgba(255,255,255,.08)" strokeWidth="1">{Array.from({ length: 9 }).map((_, i) => <line key={i} x1={i * 50} y1="0" x2={i * 50} y2="250" />)}</g>
    </svg>
  );
}

// ----------------------------------------------------------------------------
// Glass — the surfaces that float on the aurora
// ----------------------------------------------------------------------------

/**
 * One glass definition, three weights. Text sits on `glassPanel` or heavier:
 * ink on a bright wash through thin glass loses contrast fast, so the fix is
 * always MORE GLASS behind the text, never darker type.
 *
 * `backdrop-blur` is rationed — only hero surfaces get it (the blur budget is
 * what taxes a cheap Chromebook). Over an already-soft aurora, plain
 * translucency is visually indistinguishable and strictly cheaper.
 */
export const glassPanel =
  'rounded-[22px] border border-white/70 bg-white/[0.78] shadow-[0_18px_44px_-28px_rgba(25,24,49,0.42)] ' +
  'dark:border-white/[0.10] dark:bg-[#181A2A]/[0.72] dark:shadow-[0_18px_44px_-24px_rgba(0,0,0,0.7)]';

/** Hero surfaces only — carries the real backdrop blur. */
export const glassHero =
  'rounded-[26px] border border-white/75 bg-white/[0.72] backdrop-blur-xl shadow-[0_28px_70px_-34px_rgba(25,24,49,0.5)] ' +
  'supports-[not(backdrop-filter:blur(1px))]:bg-white/[0.93] ' +
  'dark:border-white/[0.12] dark:bg-[#181A2A]/[0.66] dark:supports-[not(backdrop-filter:blur(1px))]:bg-[#181A2A]/[0.95]';

/** Quiet chips, pills and inline tags. */
export const glassChip =
  'rounded-full border border-white/70 bg-white/[0.72] dark:border-white/[0.10] dark:bg-white/[0.08]';

/** Muted body text that still clears 4.5:1 on glass over the wash. */
export const inkMuted = 'text-[#575277] dark:text-[#A8A5C4]';

// ----------------------------------------------------------------------------
// Brand top bar + theme toggle
// ----------------------------------------------------------------------------

export function ThemeToggle({ theme, onToggle }: { theme: LearnTheme; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      aria-label="Toggle theme"
      className={cn('grid h-9 w-9 shrink-0 place-items-center transition-colors hover:text-[#191831] dark:hover:text-white', glassChip, inkMuted)}
    >
      {theme === 'dark' ? <Moon className="h-[18px] w-[18px]" /> : <Sun className="h-[18px] w-[18px]" />}
    </button>
  );
}

export function BrandMark() {
  return (
    <svg width="50" height="30" viewBox="0 0 60 36" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="4K Labs" className="shrink-0">
      <rect width="60" height="36" rx="8" fill="url(#lab_grad_learn)" />
      <text x="8" y="25" fill="white" fontSize="20" fontWeight="bold" fontFamily="system-ui, sans-serif">4K</text>
      <path d="M38 16L42 18L38 20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M45 16L49 18L45 20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <defs><linearGradient id="lab_grad_learn" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#8B5CF6" /><stop offset="100%" stopColor="#7C3AED" /></linearGradient></defs>
    </svg>
  );
}

export type LearnTab = 'discover' | 'library' | 'insights';
const TABS: LearnTab[] = ['discover', 'library', 'insights'];

export function LearnTopBar({ theme, onToggle, right, activeTab, onTab }: {
  theme: LearnTheme; onToggle: () => void; right?: React.ReactNode;
  activeTab?: LearnTab; onTab?: (t: LearnTab) => void;
}) {
  return (
    <header className="sticky top-0 z-40 px-4 pt-4 sm:px-6">
      <div className={cn('learn-shell mx-auto flex h-[64px] items-center gap-4 px-4 sm:px-5', glassHero, 'rounded-full')}>
        {/* The whole lockup goes home to the library, not to the marketing site.
            A learner tapping the logo mid-lesson expects to get back to their
            lessons; sending them off the platform loses their place and, on a
            demo, loses the room. */}
        <Link
          to="/learn"
          aria-label="4K Labs Learn — all lessons"
          className="flex items-center gap-2.5 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5]"
        >
          <BrandMark />
          <span className="font-serif text-[21px] tracking-tight">Learn</span>
        </Link>

        {onTab && (
          <nav className="ml-2 hidden gap-0.5 sm:flex">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => onTab(t)}
                className={cn(
                  'rounded-full px-4 py-2 text-sm capitalize transition-colors',
                  activeTab === t
                    ? 'bg-[#5B2EE5] font-semibold text-white shadow-[0_6px_18px_-8px_rgba(91,46,229,0.9)]'
                    : cn('font-medium hover:text-[#191831] dark:hover:text-white', inkMuted),
                )}
              >
                {t}
              </button>
            ))}
          </nav>
        )}

        <div className="flex-1" />
        {right}
        <ThemeToggle theme={theme} onToggle={onToggle} />
      </div>
    </header>
  );
}

// Reaction vocabulary — keep in sync with experienceFeedbackService.ts ALLOWED_REACTIONS.
export const REACTIONS: { key: string; label: string }[] = [
  { key: 'clear', label: 'Clear & easy to follow' },
  { key: 'made-me-think', label: 'Made me think' },
  { key: 'loved-interaction', label: 'Loved the interaction' },
  { key: 'too-fast', label: 'Too fast' },
  { key: 'want-more', label: 'Want more like this' },
];

/** Stable anonymous device key for feedback/review attribution. */
export function getReviewerKey(): string {
  try {
    let k = window.localStorage.getItem('learn-reviewer-key');
    if (!k) {
      k = (window.crypto?.randomUUID?.() ?? `rk_${Math.abs(hashStr(String(performance.now()) + navigator.userAgent))}`);
      window.localStorage.setItem('learn-reviewer-key', k);
    }
    return k;
  } catch {
    return 'anon';
  }
}
function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return h;
}
